import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Modal from '../Global/Modals/Modal';
import TextFieldInput from '../Global/Inputs/TextField';
import ProgressButton from '../Global/Buttons/Progress';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import { Formik, FastField } from 'formik';
import Grid from '@material-ui/core/Grid';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import ButtonStyles from '../../styles/button';
import SpacingStyles from '../../styles/helpers/spacing';

import getCancellationReasonModel from '../../Models/CancellationReason';
import FormSchema from './Form.schema';

const styles = theme => ({
  titleContainer: {
    marginBottom: `${theme.spacing.unit * 4.125}px !important`,
    position: 'relative'
  },
  title: {
    fontWeight: 'bold !important'
  },
  fieldsMarginBottom: {
    marginBottom: `${theme.spacing.unit * 2.5}px !important`
  },
  toggleSwitch: {
    alignItems: 'center',
    display: 'flex',
    position: 'absolute',
    top: -10,
    right: 0
  },
  toggleSwitchLeftLabel: {
    marginRight: `${theme.spacing.unit * 1.5}px !important`
  },
  toggleSwitchLabel: {
    color: `${theme.typography.color.second} !important`
  }
});

export class Form extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
  };

  state = {};

  async componentDidMount() {
    this.setState({ ...getCancellationReasonModel(), isNew: false });
  }

  componentDidUpdate(prevProps) {
    if (this.props.item !== prevProps.item) {
      this.setState({
        ...this.props.item,
        isNew: !!this.props.item.id
      });
    }
  }

  _handleOnClose = () => {
    this.props.onClose();
  };

  _handleOnSave = async values => {
    const { onSave } = this.props;
    try {
      await onSave(values);
      return true;
    } catch {
      return false;
    }
  };

  render() {
    const { classes, open, t, isSubmitting } = this.props;
    const { ...cancellationReason } = this.state;
    return (
      <Formik
        validationSchema={FormSchema({ t })}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          ...cancellationReason
        }}
        onSubmit={async (values, actions) => {
          const saved = await this._handleOnSave(values);
          if (saved) {
            actions.resetForm();
            this._handleOnClose();
          }
        }}
        onReset={(values, actions) => {
          actions.resetForm();
          this._handleOnClose();
        }}
        render={({
          values,
          handleChange,
          handleSubmit,
          handleReset,
          dirty
        }) => (
          <Modal
            open={open}
            onClose={handleReset}
            body={
              <Fragment>
                <div className={classes.titleContainer}>
                  <p className={classnames(classes.headline6, classes.title)}>
                    {`${
                      values.isNew
                        ? t('Edit Cancellation Reason')
                        : t('Add Cancellation Reason')
                    }`}
                  </p>
                  {values.isNew && (
                    <div className={classes.toggleSwitch}>
                      <p
                        className={classnames(classes.body2, {
                          [classes.toggleSwitchLabel]: values.expired
                        })}
                      >
                        {t('Inactive')}
                      </p>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.expired || false}
                            onChange={({ target }) => {
                              handleChange({
                                target: {
                                  name: target.value,
                                  value: target.checked
                                }
                              });
                            }}
                            value="expired"
                            color="primary"
                          />
                        }
                        label={t('Active')}
                        classes={{
                          root: classes.noMargin,
                          label: classnames(classes.body2, {
                            [classes.toggleSwitchLabel]: !values.expired
                          })
                        }}
                      />
                    </div>
                  )}
                </div>
                <Grid container spacing={16}>
                  <Grid item xs={12}>
                    <FastField
                      name="name"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.name}
                            label={t('Name')}
                            fullWidth
                            field={field}
                            form={form}
                            classes={{
                              root: classes.fieldsMarginBottom
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="description"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.description}
                            label={t('Description')}
                            fullWidth
                            field={field}
                            form={form}
                            classes={{
                              root: classes.noMarginBottom
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </Fragment>
            }
            footer={
              <Fragment>
                <Button
                  className={classes.button}
                  onClick={handleReset}
                  color={'primary'}
                  classes={{ label: classes.buttonLabel }}
                >
                  {t('Cancel')}
                </Button>
                <ProgressButton
                  onClick={handleSubmit}
                  className={classes.button}
                  classes={{
                    disabled: classes.buttonDisabled,
                    label: classes.buttonLabel
                  }}
                  color={'primary'}
                  disabled={!dirty || isSubmitting}
                  isSubmitting={isSubmitting}
                >
                  {values.isNew ? t('Save') : t('Add')}
                </ProgressButton>
              </Fragment>
            }
          />
        )}
      />
    );
  }
}
const translatedForm = withNamespaces()(Form);
export default withStyles(
  combineStyles(
    styles,
    LayoutStyles,
    TypographyStyles,
    ButtonStyles,
    SpacingStyles
  )
)(translatedForm);
